import { useEffect, useMemo, useRef, useState } from "react";
import { POSTER_PAGE, type PosterDoc, type PosterStateResponse } from "@studio/shared";
import { loadPosterFonts } from "./fonts";
import { canvasMeasure, layoutText } from "./geometry";
import { PosterElementView, type ImageResolver } from "./PosterElementView";
import { starterDoc } from "./template";
import { withAutoQr } from "./autoQr";

export function imageResolverFor(state: Pick<PosterStateResponse, "cuts" | "photos">): ImageResolver {
  return (source) => source.kind === "slot"
    ? state.cuts.find((cut) => cut.slot === source.slot)?.image_url ?? null
    : state.photos.find((photo) => photo.asset_id === source.asset_id)?.url ?? null;
}

/** 폭에 맞춰 그리는 읽기 전용 포스터(이어하기·교사 진행판·확정 화면). QR 은 구석에 자동으로 붙는다(autoQr). */
export function PosterPreview({ state, doc }: { state: PosterStateResponse; doc?: PosterDoc | null }) {
  const box = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [fontsReady, setFontsReady] = useState(false);
  useEffect(() => {
    void loadPosterFonts().then(() => setFontsReady(true));
  }, []);
  useEffect(() => {
    const node = box.current;
    if (!node) return undefined;
    const observer = new ResizeObserver(() => setWidth(node.clientWidth));
    observer.observe(node);
    setWidth(node.clientWidth);
    return () => observer.disconnect();
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const shown = useMemo(() => withAutoQr(doc ?? state.doc ?? starterDoc(state)), [doc, state.doc, state.cuts]);
  const scale = width / POSTER_PAGE.width;
  const layouts = useMemo(() => new Map(shown.els.flatMap((element) =>
    element.type === "text" ? [[element.id, layoutText(element, canvasMeasure)] as const] : []
  // eslint-disable-next-line react-hooks/exhaustive-deps
  )), [shown, fontsReady]);
  const resolve = imageResolverFor(state);
  return (
    <div ref={box} className="poster-page" style={{ background: shown.bg, aspectRatio: `${POSTER_PAGE.width} / ${POSTER_PAGE.height}` }}>
      {width > 0 ? shown.els.map((element) => (
        <PosterElementView
          key={element.id}
          element={element}
          scale={scale}
          layout={layouts.get(element.id)}
          resolveImage={resolve}
          qrUrl={state.ar_url}
          dataId={false}
        />
      )) : null}
    </div>
  );
}
